export class PatchAssetParam {
  constructor(public fleetId: string) {}
}

export enum Packages {
  dvr = 'DVR',
}

enum CameraType {
  ridecam = 'RIDECAM',
  ridecamPlus = 'RIDECAM_PLUS',
}

interface MdvrChannelMapping {
  fps?: number; // Not required as of now
  resolution?: string; // No need to send of CONVOY since only one resolution is supported
  source: string;
  view: string;
  hflip?: boolean;
  vflip?: boolean;
}

export interface MdvrConfig {
  serialId?: string;
  wifiSsid?: string;
  channelMappings: MdvrChannelMapping[];
  mdvrConfigUpdatedAt: string;
}

export interface AssetDetails {
  active: boolean;
  assetId: string;
  fleetId: string;
  dutyType: string;
  assetName?: string;
  cameraType?: CameraType;
  pilotActive?: boolean;
  pilotAvailable?: boolean;
  start?: string;
  packages?: string[];
  plusPackages?: string[];
  ridecamPlusPlan?: string;
  recurringLivestreamExtraMinutes?: number;
  end?: string;
  tags?: string[];
  metadata?: { [key: string]: string };
  mdvrConfig?: MdvrConfig;
  deviceId?: string;
  defaultDriverId?: string;
  serialNumber?: string;
  customPackages?: string[];
}

export interface Device {
  assetId: string | null;
  deviceId: string;
  serialNumber: string | null;
  deviceModel: string;
  fleetId: string | null;
  provisioned: boolean;
  provisionedAt: string | null;
  vendorId: string;
  assetName?: string;
  asset?: AssetDetails;
  deprovisionLoader?: boolean;
  firmware1Semver?: string;
  firmware2Semver: string;
}

export class DeprovisionDeviceReqBody {
  public vendorId: string;
  public fleetId: string;
  public deviceId: string;
  public assetId: string;
  public retainFleetId: boolean;

  constructor(device: Device) {
    this.vendorId = device.vendorId;
    this.fleetId = device.fleetId;
    this.deviceId = device.deviceId;
    this.assetId = device.assetId;
    this.retainFleetId = true;
  }
}

export interface Asset {
  assetId: string;
  deviceId: string;
}

export interface EditDeviceInfo {
  vendorId: string;
  fleetId: string;
  deviceIds: string[];
  assetIds: string[];
  newProvision: boolean;
  dutyType?: string;
  ridecamPlusPlan?: string;
}

export class ManageDeviceBodyParams {
  public sdCardNumber?: string;
}

export class ManageDevicequeryParams {
  public action: string;
}

export class getDeviceTaskReqParams {
  public taskType: string;
}

export class TriggerDeviceTaskReqParams {
  public taskType: string;
  public temporaryTripBlocking: boolean;
  public temporaryTripBlockingEndTimestamp?: string;
}

export class ManageDeviceReqBody {
  public deviceId: string;
  public action: string;
}

export interface EditProvisionDeviceInfo {
  assetId: string;
  dutyType?: string;
  defaultDriverId?: string;
}

export interface UpdateMdvrConfigReqBody {
  channelMappings: MdvrChannelMapping[];
  serialId?: string;
}

export interface AssetPlans {
  plans: {
    [key: string]: string[];
  };
  customPackages: {
    STANDARD: string[];
  };
}

export interface MdvrCameraConfig {
  sources: string[];
  resolution: {
    enum: string[];
    default: string;
  };
  fps: {
    enum: string[];
    default: string;
  };
  hflip?: boolean;
  vflip?: boolean;
  cameraInterface: 'CONVOY' | 'TVI' | 'UVC' | 'TWI';
  rotateInDegrees?: {
    enum: number[];
    default: number;
  };
}

export interface DeviceModelConfig {
  mdvrCameras: MdvrCameraConfig[];
  deviceModel: string;
}

export interface InstallationQueryParams {
  assetId: string;
  deviceId: string;
}

export interface InstallationReportDetails {
  deviceId: string;
  assetId: string;
  dutyType: string;
  fleetId: string;
  installationDetails: installationDetails;
  clientId?: string;
  createDate?: string;
  createDateUTC?: string;
}

export interface InstallationStorageDetails {
  used: number;
  slotId: number;
  totalStorage: number;
  sdcardFormatted?: boolean;
}

export interface installationDetails {
  sdCards: InstallationStorageDetails[];
  diagnosticsReport: InstallationDiagnosticsReport;
  mountingDetails: InstallationMountingDetails[];
  externalCameras: ExternalCameraDetail[];
  troubleshootedIssues?: InstallationTroubleShootedIssues[];
  installationStartTimestamp: string;
  installationStartTimestampUTC: string;
  installationEndTimestamp: string;
  installationEndTimestampUTC: string;
  installationDurationInMins: number;
  didRebootDashcam: boolean;
  driverSideSelection: string;
  defaultDriverId?: string;
  defaultDriverName?: string;
  installerName: string;
  installerEmail: string;
  sdkVersion: string;
  firmwareversion: string;
  alertVolume: number;
  preferredAPN?: string;
  powerSource?: string;
}

export interface InstallationDiagnosticsReport {
  totalIssues: number;
  issues: installationIssue[];
}

export interface installationIssue {
  eventType: string;
  reported?: boolean;
  timestamp?: string;
  timestampUTC: string;
}

export interface InstallationMountingDetails {
  cameraType: string;
  fileName: string;
  imageUrl: string;

  view: string;
  source: string;
}

export interface ExternalCameraDetail {
  view: string;
  hflip: boolean;
  vflip: boolean;
  source: string;
  rotation: number;
  resolution: string;
  configured: boolean;
  connected: boolean;
}

export interface InstallationTroubleShootedIssues {
  eventType: string;
  eventName?: string;
}

//diagnostics
export interface HealthEventAggregateResponse {
  categoryAggregate: eventAggregate;
}

export interface eventAggregate {
  PowerCutAlert?: number;
  DeviceOffline?: number;
  UnprovisionedDeviceDetected?: number;
  SDCardNeedFormatting?: number;
  ExternalStorageAvailable?: number;
}

export interface CameraEventAggregateResponse {
  categoryAggregate: cameraeventAggregate;
}

export interface cameraeventAggregate {
  BadMountingRoadCamera?: number;
  BadMountingDriverCamera?: number;
  RoadCameraLensCovered?: number;
  DriverCameraLensCovered?: number;
  BadMountingInertialSensor?: number;
}

export interface HealthEventListResponse {
  limit: number;
  skip: number;
  totalCount: number;
  rows: healthListRow[];
}

export interface healthListRow {
  assetId?: string;
  fleetId?: string;
  deviceId?: string;
  eventType?: string;
  timestamp?: string;
  timestampUTC?: string;
}

export interface CameraEventsResponse {
  rows: cameraEventTypeList[];
  limit: number;
  skip: number;
  totalCount: number;
}
export interface cameraEventTypeList {
  assetId?: string;
  fleetId?: string;
  deviceId?: string;
  eventType?: string;
  mediaFiles?: mediaFileType[];
  timestamp?: string;
  timestampUTC?: string;
}

export interface mediaFileType {
  source?: string;
  mediaFile?: string;
  mediaType?: string;
  imageDetails?: {
    imageType?: string;
    imageWidth?: number;
    imageHeight?: number;
    includesPII?: boolean;
    piiSubframe?: {
      width?: number;
      height?: number;
      topLeftX?: number;
      topLeftY?: number;
    };
    imageQuality?: number;
    imageCaptureState?: string;
  };
  mediaFileIndex?: number;
}
export interface categoryInterface {
  category: string;
  categoryId: number;
}

export interface GetDevicePingListResponseRow {
  assetId: null | string;
  fleetId: null | string;
  deviceId: string;
  daysInactive: number;
  timestampUTC: string;
}

export interface GetDevicePingListResponse {
  rows: GetDevicePingListResponseRow[];
  limit: number;
  totalCount: number;
  skip: number;
}

export interface IncorrectCameraMountingAsset {
  assetId: string;
  fleetId: string;
  link: string;
  timestamp: string;
}

export interface CategoryListResponse {
  healthEventTitle: string[];
  healthEventTroubleshootingGuides: healthEventTroubleshootingGuides;
}

export interface healthEventTroubleshootingGuides {
  guide: string;
  summary: string[];
}

export interface CamerOnlineStatusParams {
  assetId?: string;
  deviceId?: string;
  sortBy?: string;
  sort?: string;
  limit?: number;
  offset?: number;
  daysInactive?: string;
  startDate: string;
}

export interface HealthCheckGlossaryParam {
  healthEventId: string;
  language?: string;
}
export interface HealthEventParams {
  eventType?: string;
  assetId?: string;
  deviceId?: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sort?: string;
  startDate: string;
}
export interface AggregateParam {
  startDate: string;
}

export interface DiagnosticsStatstResponse {
  deviceCount: number;
}
export interface DiagnosticsTotalCurrentIssueResponse {
  totalDeviceCount: number;
}

export interface DeviceDetailsResponse {
  deviceModel: string;
  vendorId: string;
  provisioned: boolean;
  fleetName: string;
  status?: string;
  reportUpdatedAt?: Date;
  suppressed?: boolean;
  reportIndex?: number;
  dormant?: boolean;
  dormantUpdatedAt?: Date;
  issueCount?: number;
  deviceMetadata?: DeviceMetadata;
  clientId?: string;
  fleetId?: string;
  assetId?: string | null;
  deviceId?: string | null;
  provisionedAt?: string | null;
  serialNumber?: string | null;
}

export interface DeviceMetadata {
  dutyType?: string;
  ridecamPlusPlan?: string;
  recurringLivestreamExtraMinutes?: number;
}

export interface RegisteredDriverListResponse {
  data: DriverListData[];
}

export interface DriverListData {
  driverId: string;
  userType: string;
  accountStatus: string;
  username: string;
  driverName?: string;
  email?: string;
  personId?: string;
}

export interface DeviceListResponse {
  devices: DeviceDetails[];
  limit: number;
  skip: number;
}

export interface DeviceDetails {
  deviceId: string;
  deviceModel: string;
  vendorId: string;
  fleetId: string;
  assetId: string;
  provisionedAt: string;
  provisioned: boolean;
  rideviewPushChannelActive: boolean;
  updaterPushChannelActive: boolean;
}
export interface DeviceStatsParams {
  semiProvisioned: boolean;
}

export interface DeviceListParams {
  semiProvisioned: boolean;
  limit: number;
  offset: number;
}

export interface KeyValuePair {
  [key: string]: string;
}

export interface StringToNumberMap {
  [key: string]: number;
}

export interface GetMuteEventsParams {
  limit: number;
  offset: number;
  sort?: string;
  sortBy?: string;
  startDate?: string;
  eventType?: string;
}

export interface GetMuteEventsResponse {
  rows: MutedEvent[];
  skip: number;
  limit: number;
  totalCount: number;
  eventAggregate: StringToNumberMap;
  categoryAggregate: StringToNumberMap;
}

export interface MutedEvent {
  assetId: string;
  fleetId: string;
  deviceId: string;
  eventType: string;
  timestamp: string;
  timestampUTC: string;
  mutedTimestampUTC: string;
  mutedForDurationInDays: number;
  totalMutedDurationInHours: number;
  mediaFiles?: MutedEventMediaFile[];
}

export interface MutedEventMediaFile {
  source: string;
  mediaFile: string;
  mediaType: string;
  imageDetails?: KeyValuePair;
  videoDetails?: VideoDetails;
  mediaFileIndex: number;
}

export interface PatchMuteEventParams {
  eventType: string;
  muteForDurationInDays?: number;
  muteState: boolean;
}

export interface PatchMuteEventResponse {
  message: string;
  deviceId: string;
  eventType: string;
  muteState: boolean;
  muteForDurationInDays?: number | null;
}

export interface BadMountingCameraModelData {
  event: BadMountingCameraEvent;
  clickType: string;
  selectedTableType: string;
}

export interface BadMountingCameraEvent {
  assetId: string;
  fleetId: string;
  deviceId: string;
  eventType: string;
  timestamp: string;
  mediaFiles: MutedEventMediaFile[];
  timestampUTC: string;
  mutedTimestampUTC?: string;
  mutedForDurationInDays?: number;
  totalMutedDurationInHours?: number;
  isVideo: boolean;
  eventTypeLabel: string;
  eventVideoFilename: string;
}

export interface MuteEventDurationOption {
  noOfDays: number;
  buttonLabel: string;
  modelLabel?: string;
  group: number;
}

export interface VideoDetails {
  videoWidth: number;
  videoHeight: number;
  videoBitrate: number;
  videoQuality: number;
  videoType: string;
  videoPiPType: string;
  codec_type: string;
  audioEnabled: boolean;
  sources: string[];
  collage: string;
}

export interface SelectedFilter {
  selectedFilter: string;
  value: string;
}

export interface TableType {
  selectedTableType: string;
  refreshGraph: boolean;
  increaseCount?: boolean;
  subEvent?: string;
  unselectAll?: boolean;
}

export enum BadMountingCameraTabelClick {
  row = 'Row',
  button = 'Button',
}

export enum BadMountingCameraTableTypeEnum {
  ActiveEvents = 'activeEvents',
  MutedEvents = 'mutedEvents',
}

export enum DiagnosticsSubTabEnum {
  CameraEvents = 'cameraevent',
  HealthEvent = 'healthevent',
  CameraOnlineStatus = 'cameraonlinestatus',
}

export enum SearchTypeEnum {
  AssetIdFilter = 'assetIdFilter',
  DeviceIdFilter = 'deviceIdFilter',
}

export interface DiagnosticsQueryParams {
  tab?: string;
  subTab?: string;
  eventType?: string;
  deviceId?: string;
}
